import type { LevelConfig } from "../level_config.js";
import type { PlayDialogDifficulty } from "../ui/dialog/play_dialog.js";
import type {
  DailyChallengePayloadDto,
  DailyChallengeStageDto,
} from "../api/dto/daily_challenge.dto.js";
import type { DailyTaskRewardDto } from "../api/dto/daily_tasks.dto.js";
import {
  getDailyChallengeConditional,
  completeDailyChallengeStage,
  getDailyChallenge,
  getDailyChallengeLevel,
} from "../api/requests/daily_challenge.requests.js";
import { CumulativeBoosterService } from "../cumulative_boosters/cumulative_booster_service.js";
import { StatisticsStorage } from "../storage/statistics_storage.js";
import { infiniteHeartsUntilMs, serverUntilMs } from "../api/player_sync.js";
import { DAILY_CHALLENGE_DAYS_COUNT } from "./daily_challenge_config.js";
import { getDailyChallengeRewards } from "./daily_challenge_rewards.js";
import { parsePayloadBarriers, parsePayloadLayers, parsePayloadElevator, parsePayloadHang, parsePayloadShape, parsePayloadTables, parsePayloadTilt, parsePayloadTopScale } from "../level_payload.js";
import { NetworkManager } from "../network_manager.js";
import { isNetworkishError } from "../utils/network_errors.js";
import { PersistentCache } from "../offline/persistent_cache.js";
import { EventPayloadListeners, eventPayloadKey, type EventPayloadListener } from "../api/event_payload_key.js";
import { OfflineJournal } from "../offline/offline_journal.js";
import { applyLocalEventUnlock } from "../offline/local_event_unlock.js";
import { shouldGoLocal } from "../offline/offline_policy.js";
import { OfflineEconomy } from "../offline/offline_economy.js";
import { TournamentService } from "../tournament/tournament_service.js";

const CACHE_TTL_MS = 30_000;
const EXPIRED_RETRY_MS = 15_000;
const PERSIST_KEY = "daily_challenge_v1";
const LEVELS_PERSIST_KEY = "daily_challenge_levels_v1";

const DIFFICULTY_BY_NAME: Record<string, number> = {
  Common: 0,
  Hard: 1,
  SuperHard: 2,
};

export interface DailyChallengeLoadedLevel
{
  stage: number;
  uid: string;
  config: LevelConfig;
  shots: number | null;
}

interface PersistedChallenge
{
  payload: DailyChallengePayloadDto;
  period_end_at: number;
  etag?: string | null;
}

interface PersistedStageLevel
{
  uid: string;
  difficulty: string;
  shots: number | null;
  config: Record<string, unknown>;
}

interface PersistedStageLevels
{
  date: string;
  levels: Record<string, PersistedStageLevel>;
}

/**
 * Server-driven Daily Challenge: the daily grid (one level per stage, rotated
 * at UTC midnight) and the player's stage progress live on the server; this
 * facade fetches state, loads the current stage's level config and reports
 * completions. The server grants the rewards; the local
 * daily_challenge_completed_days copy is kept in sync so the screen's
 * catch-up animation keeps working from its seen/completed pair.
 */
class DailyChallengeServiceImpl
{
  private payload: DailyChallengePayloadDto | null = null;
  private etag: string | null = null;
  private fetched_at = 0;
  private period_end_at = 0;
  private readonly listeners = new EventPayloadListeners<DailyChallengePayloadDto>();
  private pending_fetch: Promise<DailyChallengePayloadDto | null> | null = null;
  private loaded_level: DailyChallengeLoadedLevel | null = null;
  private prefetching: Promise<void> | null = null;

  constructor()
  {
    const stored = PersistentCache.read<PersistedChallenge>( PERSIST_KEY );
    if ( stored?.payload && typeof stored.payload === "object" && Array.isArray( stored.payload.stages ) )
    {
      this.payload = stored.payload;
      this.etag = typeof stored.etag === "string" ? stored.etag : null;
      this.period_end_at = Number( stored.period_end_at ) || 0;
      this.fetched_at = 0;
    }
  }

  public fetchedAgoMs(): number
  {
    return this.fetched_at > 0 ? Date.now() - this.fetched_at : Number.POSITIVE_INFINITY;
  }

  public getCached(): DailyChallengePayloadDto | null
  {
    this.unlockLocally();
    return this.payload;
  }

  public onChange( listener: EventPayloadListener<DailyChallengePayloadDto> ): () => void
  {
    return this.listeners.add( listener );
  }

  private unlockLocally(): void
  {
    if ( applyLocalEventUnlock( this.payload ) )
    {
      console.info( "[DailyChallenge] unlocked locally by level" );
      this.persist();
    }
  }

  public async fetch( force = false ): Promise<DailyChallengePayloadDto | null>
  {
    this.unlockLocally();
    const age = Date.now() - this.fetched_at;
    const max_age = this.isPeriodOver() ? EXPIRED_RETRY_MS : CACHE_TTL_MS;
    if ( !force && this.payload && age < max_age )
      return this.payload;

    if ( !NetworkManager.isOnline() )
      return this.payload;

    if ( this.pending_fetch ) return this.pending_fetch;

    this.pending_fetch = this.fetchInternal();
    try
    {
      return await this.pending_fetch;
    }
    finally
    {
      this.pending_fetch = null;
    }
  }

  private async fetchInternal(): Promise<DailyChallengePayloadDto | null>
  {
    try
    {
      const res = await getDailyChallengeConditional( this.etag );
      this.fetched_at = Date.now();
      if ( res.status === 304 )
        return this.payload;

      const payload = res.data;
      const changed = !this.payload || eventPayloadKey( this.payload ) !== eventPayloadKey( payload );
      this.payload = payload;
      this.etag = res.etag;
      this.period_end_at = this.fetched_at + payload.seconds_until_reset * 1000;
      this.syncLocalProgress( payload.completed_stages );
      this.persist();
      if ( changed )
        this.listeners.notify( payload );
      return payload;
    }
    catch ( e )
    {
      console.warn( "Daily challenge fetch failed:", e );
      return this.payload;
    }
  }

  public isPeriodOver(): boolean
  {
    return this.period_end_at > 0 && Date.now() >= this.period_end_at;
  }

  public getSecondsUntilReset(): number | null
  {
    if ( this.period_end_at <= 0 ) return null;
    return Math.max( 0, Math.ceil( ( this.period_end_at - Date.now() ) / 1000 ) );
  }

  public getCompletedStages(): number
  {
    if ( this.payload ) return this.payload.completed_stages;
    return StatisticsStorage.load().daily_challenge_completed_days;
  }

  public isFinished(): boolean
  {
    if ( this.payload ) return this.payload.finished;
    return StatisticsStorage.load().daily_challenge_completed_days >= DAILY_CHALLENGE_DAYS_COUNT;
  }

  public getStage( stage: number ): DailyChallengeStageDto | null
  {
    return this.payload?.stages.find( entry => entry.stage === stage ) ?? null;
  }

  /** Server rewards when known; the bundled table is the offline fallback. */
  public getRewards( stage: number ): DailyTaskRewardDto[]
  {
    const server = this.getStage( stage )?.rewards;
    if ( server && server.length > 0 ) return server;
    return getDailyChallengeRewards( stage ).map( reward => ( { type: reward.type, amount: reward.amount } ) );
  }

  public async loadLevel( stage: number ): Promise<DailyChallengeLoadedLevel | null>
  {
    if ( this.loaded_level && this.loaded_level.stage === stage && !this.isPeriodOver() )
      return this.loaded_level;

    if ( !NetworkManager.isOnline() )
      return this.loadLevelFromCache( stage );

    try
    {
      const response = await getDailyChallengeLevel( stage );
      const loaded = this.toLoadedLevel( stage, response.level );
      if ( !loaded )
      {
        console.error( `[DailyChallenge] level for stage ${stage} has no usable layout` );
        return null;
      }
      this.storeStageLevel( response.date, stage, response.level );
      this.loaded_level = loaded;
      return this.loaded_level;
    }
    catch ( e )
    {
      console.warn( `Daily challenge level load failed (stage ${stage}):`, e );
      return isNetworkishError( e ) ? this.loadLevelFromCache( stage ) : null;
    }
  }

  private loadLevelFromCache( stage: number ): DailyChallengeLoadedLevel | null
  {
    if ( this.isPeriodOver() ) return null;
    const stored = this.readStoredLevels();
    if ( !stored || stored.date !== this.payload?.date ) return null;
    const entry = stored.levels[String( stage )];
    if ( !entry ) return null;
    const loaded = this.toLoadedLevel( stage, entry );
    if ( loaded ) this.loaded_level = loaded;
    return loaded;
  }

  public getLoadedLevel( stage: number ): DailyChallengeLoadedLevel | null
  {
    return this.loaded_level && this.loaded_level.stage === stage ? this.loaded_level : null;
  }

  public isStageAvailableOffline( stage: number ): boolean
  {
    if ( this.isPeriodOver() || !this.payload ) return false;
    const stored = this.readStoredLevels();
    return !!stored && stored.date === this.payload.date && !!stored.levels[String( stage )];
  }

  public prefetchStageLevels(): Promise<void>
  {
    if ( this.prefetching ) return this.prefetching;

    this.prefetching = ( async () =>
    {
      const payload = this.payload;
      if ( !( payload?.enabled || payload?.locked ) || !NetworkManager.isOnline() || this.isPeriodOver() ) return;

      const stored = this.readStoredLevels();
      const have = stored && stored.date === payload.date ? stored.levels : {};
      for ( const stage of payload.stages )
      {
        if ( stage.completed || stage.level === null || have[String( stage.stage )] ) continue;
        if ( !NetworkManager.isOnline() ) return;
        try
        {
          const response = await getDailyChallengeLevel( stage.stage );
          this.storeStageLevel( response.date, stage.stage, response.level );
        }
        catch ( e )
        {
          console.warn( `[DailyChallenge] prefetch of stage ${stage.stage} failed`, e );
          if ( isNetworkishError( e ) ) return;
        }
      }
    } )().finally( () => { this.prefetching = null; } );

    return this.prefetching;
  }

  public async complete( stage: number ): Promise<boolean>
  {
    if ( shouldGoLocal() )
      return this.completeLocally( stage );

    try
    {
      const response = await completeDailyChallengeStage( stage );
      this.applyBalances( response.coins, response.hearts, response.boosters, response.infinite_hearts_until, response.no_ads_until );
      this.syncLocalProgress( response.completed_stages );
      this.applyStageProgress( response.completed_stages, response.finished );
      this.loaded_level = null;
      TournamentService.applyWinReport( response.tournament );
      return true;
    }
    catch ( e )
    {
      if ( isNetworkishError( e ) )
        return this.completeLocally( stage );
      console.warn( `Daily challenge complete failed (stage ${stage}):`, e );
      void this.fetch( true );
      return false;
    }
  }

  private completeLocally( stage: number ): boolean
  {
    this.unlockLocally();
    const payload = this.payload;
    if ( !payload || !payload.enabled || this.isPeriodOver() ) return false;
    if ( stage !== payload.completed_stages + 1 ) return false;

    const finished = stage >= DAILY_CHALLENGE_DAYS_COUNT;
    OfflineEconomy.grantRewards( this.getRewards( stage ) );
    this.syncLocalProgress( stage );
    this.applyStageProgress( stage, finished );
    OfflineJournal.append( "daily_challenge_complete", { stage, date: payload.date } );
    this.loaded_level = null;
    return true;
  }

  private applyStageProgress( completed_stages: number, finished: boolean ): void
  {
    if ( !this.payload ) return;
    this.payload.completed_stages = completed_stages;
    this.payload.finished = finished;
    for ( const entry of this.payload.stages )
    {
      entry.completed = entry.stage <= completed_stages;
      entry.current = entry.stage === completed_stages + 1;
      entry.playable = entry.current && entry.level !== null;
    }
    this.persist();
  }

  private toLoadedLevel( stage: number, level: PersistedStageLevel ): DailyChallengeLoadedLevel | null
  {
    const config = this.toConfig( stage, level.difficulty, level.config );
    if ( !config ) return null;
    return { stage, uid: level.uid, config, shots: level.shots };
  }

  private toConfig( stage: number, difficulty: string, raw: Record<string, unknown> ): LevelConfig | null
  {
    if ( !raw || typeof raw !== "object" )
      return null;

    const tables = parsePayloadTables( raw.tables, "DailyChallenge" );
    const layers = tables.length > 0 ? tables[0].layers : parsePayloadLayers( raw.layers );
    if ( layers.length === 0 )
      return null;

    return {
      level: stage,
      difficulty: ( DIFFICULTY_BY_NAME[difficulty] ?? DIFFICULTY_BY_NAME.SuperHard ) as PlayDialogDifficulty,
      layers,
      ...parsePayloadTilt( raw ),
      ...parsePayloadShape( raw ),
      ...parsePayloadHang( raw ),
      ...parsePayloadElevator( raw ),
      ...parsePayloadTopScale( raw ),
      ...parsePayloadBarriers( raw, "DailyChallenge" ),
      ...( tables.length > 0 ? { tables } : {} ),
    };
  }

  private syncLocalProgress( completed_stages: number ): void
  {
    const clamped = Math.max( 0, Math.min( DAILY_CHALLENGE_DAYS_COUNT, completed_stages ) );
    const stats = StatisticsStorage.load();
    if ( stats.daily_challenge_completed_days === clamped ) return;
    StatisticsStorage.update( {
      daily_challenge_completed_days: clamped,
      ...( clamped < stats.daily_challenge_seen_completed_days
        ? { daily_challenge_seen_completed_days: clamped }
        : {} ),
    } );
  }

  private applyBalances(
    coins: number,
    hearts: number,
    boosters: Parameters<typeof CumulativeBoosterService.applyInventory>[0],
    infinite_hearts_until?: string | null,
    no_ads_until?: string | null
  ): void
  {
    const stats = StatisticsStorage.load();
    stats.coins = coins;
    stats.hearts = Math.max( stats.hearts, hearts );
    stats.infinite_hearts_until = Math.max(
      stats.infinite_hearts_until,
      infiniteHeartsUntilMs( infinite_hearts_until )
    );
    stats.no_ads_until = Math.max( stats.no_ads_until, serverUntilMs( no_ads_until ) );
    StatisticsStorage.save( stats );
    CumulativeBoosterService.applyInventory( boosters );
  }

  private persist(): void
  {
    if ( !this.payload ) return;
    const stored: PersistedChallenge = { payload: this.payload, period_end_at: this.period_end_at, etag: this.etag };
    PersistentCache.write( PERSIST_KEY, stored );
  }

  private readStoredLevels(): PersistedStageLevels | null
  {
    const stored = PersistentCache.read<PersistedStageLevels>( LEVELS_PERSIST_KEY );
    return stored && typeof stored.date === "string" && stored.levels && typeof stored.levels === "object" ? stored : null;
  }

  private storeStageLevel( date: string, stage: number, level: PersistedStageLevel ): void
  {
    const stored = this.readStoredLevels();
    const levels = stored && stored.date === date ? stored.levels : {};
    levels[String( stage )] = { uid: level.uid, difficulty: level.difficulty, shots: level.shots, config: level.config };
    const next: PersistedStageLevels = { date, levels };
    PersistentCache.write( LEVELS_PERSIST_KEY, next );
  }
}

export const DailyChallengeService = new DailyChallengeServiceImpl();
